import React, { useState } from "react";
import { Link } from "react-router-dom";
import { submitMobileNumberRequest } from "../../api/mobileNumberRequestApi";
import { checkDob, DOB_ERROR, maskDob } from "../../utils/dob";

const WHATSAPP = "917351519573"; // support WhatsApp (same number as the site footer)

const OBJECTIVES = [
  "Career and Professional Growth",
  "Business and Entrepreneurship",
  "Financial Stability and Opportunities",
  "Marriage and Relationships",
  "Confidence and Personal Development",
  "Multiple Areas of Life",
  "Other",
];

const empty = {
  fullName: "",
  dateOfBirth: "",
  timeOfBirth: "",
  timeUnknown: false,
  placeOfBirth: "",
  currentCityState: "",
  whatsapp: "",
  email: "",
  objective: "",
  objectiveOther: "",
  consentData: false,
  consentPolicy: false,
  consentNoGuarantee: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Client-side check mirroring the API, so people see what to fix before sending.
const validate = (f) => {
  const e = {};
  if (!f.fullName.trim()) e.fullName = "Please enter your full name.";
  if (checkDob(f.dateOfBirth) !== f.dateOfBirth || !f.dateOfBirth) e.dateOfBirth = DOB_ERROR;
  if (!f.timeUnknown && !f.timeOfBirth) e.timeOfBirth = "Please enter your time of birth, or tick 'I don't know'.";
  if (!f.placeOfBirth.trim()) e.placeOfBirth = "Please enter your place of birth.";
  if (!f.currentCityState.trim()) e.currentCityState = "Please enter your current city and state.";
  if (!/^(?:\+?91|0)?[6-9]\d{9}$/.test(f.whatsapp.replace(/[\s-]/g, ""))) e.whatsapp = "Please enter a valid 10-digit WhatsApp number.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (!f.objective) e.objective = "Please choose your primary objective.";
  if (f.objective === "Other" && !f.objectiveOther.trim()) e.objectiveOther = "Please tell us a little about your objective.";
  if (!f.consentData || !f.consentPolicy || !f.consentNoGuarantee) e.consent = "Please tick all three confirmations to continue.";
  return e;
};

export default function MobileNumberRequestForm() {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setServerError("");
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) {
      const first = document.querySelector(".mnr-field.has-error input, .mnr-field.has-error select, .mnr-consent.has-error input");
      if (first) first.focus();
      return;
    }
    setSubmitting(true);
    const result = await submitMobileNumberRequest({
      fullName: form.fullName,
      dateOfBirth: form.dateOfBirth,
      timeOfBirth: form.timeUnknown ? "Not known" : form.timeOfBirth,
      placeOfBirth: form.placeOfBirth,
      currentCityState: form.currentCityState,
      whatsapp: form.whatsapp,
      email: form.email,
      objective: form.objective,
      objectiveOther: form.objective === "Other" ? form.objectiveOther : "",
      consentData: form.consentData,
      consentPolicy: form.consentPolicy,
      consentNoGuarantee: form.consentNoGuarantee,
    });
    setSubmitting(false);
    if (result.ok && result.success) {
      setDone(result.data);
      setForm(empty);
    } else {
      setServerError(result.message || "We could not submit your request. Please try again.");
    }
  };

  if (done) {
    const text = encodeURIComponent(`Hello, my Mobile Number Customization reference is ${done.ref}. I have submitted my details.`);
    return (
      <div className="mnr-done" role="status">
        <h3>Thank you, {done.fullName}</h3>
        <p>Your request has been received. Your reference number is:</p>
        <div className="mnr-ref">{done.ref}</div>
        <p>
          Please keep it safe and quote it when you make the payment and whenever you contact us. A confirmation has also been
          emailed to you. Our team will reach you on WhatsApp ({done.whatsapp}) with the next steps and payment details.
        </p>
        <a className="mnr-btn mnr-btn-gold" href={`https://wa.me/${WHATSAPP}?text=${text}`} target="_blank" rel="noopener noreferrer">
          Message us on WhatsApp
        </a>
      </div>
    );
  }

  const field = (name, label, input) => (
    <div className={`mnr-field ${errors[name] ? "has-error" : ""}`}>
      <label htmlFor={`mnr-${name}`}>{label}</label>
      {input}
      {errors[name] && <small role="alert">{errors[name]}</small>}
    </div>
  );

  return (
    <form className="mnr-form" onSubmit={submit} noValidate>
      <h3 className="mnr-section">Section A - Your Personal Details</h3>
      <div className="mnr-grid">
        {field("fullName", "Full Name *", <input id="mnr-fullName" autoComplete="name" maxLength={150} value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />)}
        {field(
          "dateOfBirth",
          "Date of Birth *",
          <input
            id="mnr-dateOfBirth"
            inputMode="numeric"
            autoComplete="bday"
            placeholder="DD/MM/YYYY"
            maxLength={10}
            value={form.dateOfBirth}
            onChange={(e) => set("dateOfBirth", maskDob(e.target.value))}
          />
        )}
        {field(
          "timeOfBirth",
          "Time of Birth *",
          <>
            <input id="mnr-timeOfBirth" type="time" disabled={form.timeUnknown} value={form.timeOfBirth} onChange={(e) => set("timeOfBirth", e.target.value)} />
            <label className="mnr-inline">
              <input type="checkbox" checked={form.timeUnknown} onChange={(e) => set("timeUnknown", e.target.checked)} /> I don&apos;t know my exact time
            </label>
          </>
        )}
        {field("placeOfBirth", "Place of Birth *", <input id="mnr-placeOfBirth" maxLength={150} value={form.placeOfBirth} onChange={(e) => set("placeOfBirth", e.target.value)} />)}
        {field("currentCityState", "Current City and State *", <input id="mnr-currentCityState" maxLength={150} autoComplete="address-level2" value={form.currentCityState} onChange={(e) => set("currentCityState", e.target.value)} />)}
        {field("whatsapp", "WhatsApp Number *", <input id="mnr-whatsapp" type="tel" inputMode="numeric" autoComplete="tel" maxLength={15} placeholder="98765 43210" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />)}
        {field("email", "Email Address *", <input id="mnr-email" type="email" autoComplete="email" maxLength={150} value={form.email} onChange={(e) => set("email", e.target.value)} />)}
      </div>

      <h3 className="mnr-section">Section B - Your Primary Objective</h3>
      <p className="mnr-hint">What would you primarily like your personalised mobile number consultation to focus on?</p>
      <div className={`mnr-field ${errors.objective ? "has-error" : ""}`}>
        <div className="mnr-radios" role="radiogroup" aria-label="Primary objective">
          {OBJECTIVES.map((o) => (
            <label key={o} className={`mnr-radio ${form.objective === o ? "is-on" : ""}`}>
              <input type="radio" name="objective" value={o} checked={form.objective === o} onChange={() => set("objective", o)} />
              {o}
            </label>
          ))}
        </div>
        {errors.objective && <small role="alert">{errors.objective}</small>}
      </div>
      {form.objective === "Other" &&
        field("objectiveOther", "Please tell us your objective *", <input id="mnr-objectiveOther" maxLength={300} value={form.objectiveOther} onChange={(e) => set("objectiveOther", e.target.value)} />)}

      <div className={`mnr-consents ${errors.consent ? "has-error" : ""}`}>
        <label className="mnr-consent">
          <input type="checkbox" checked={form.consentData} onChange={(e) => set("consentData", e.target.checked)} />
          <span>
            I confirm that the information provided is accurate to the best of my knowledge. I agree to be contacted by Vaidik Vidhya™
            regarding my request and understand that my information will be handled in accordance with the applicable{" "}
            <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>.
          </span>
        </label>
        <label className="mnr-consent">
          <input type="checkbox" checked={form.consentPolicy} onChange={(e) => set("consentPolicy", e.target.checked)} />
          <span>
            I have read and agree to the{" "}
            <Link to="/mobile-numerology-policy" target="_blank">Mobile Numerology Consultation Policy</Link>, including the cancellation
            and refund terms.
          </span>
        </label>
        <label className="mnr-consent">
          <input type="checkbox" checked={form.consentNoGuarantee} onChange={(e) => set("consentNoGuarantee", e.target.checked)} />
          <span>
            I understand that astrology and numerology are traditional interpretive practices and that a mobile number does not guarantee
            any particular personal, professional, financial or other outcome.
          </span>
        </label>
        {errors.consent && <small role="alert">{errors.consent}</small>}
      </div>

      {serverError && (
        <div className="mnr-error" role="alert">
          {serverError}
        </div>
      )}
      <button className="mnr-btn mnr-btn-gold mnr-submit" type="submit" disabled={submitting}>
        {submitting ? "Submitting..." : "SUBMIT MY DETAILS"}
      </button>
    </form>
  );
}
